package com.fireavert.react.pages.administration.breadcrumbs.yardi_integration

import YardiSavePropertyDataRequest
import com.fireavert.administration.yardi_integration_page.yardi_main_page.client.presentation.YardiCommonDataController
import com.fireavert.administration.yardi_integration_page.yardi_main_page.client.presentation.YardiCommonDataViewModel
import com.fireavert.logging.Logger
import com.fireavert.menu.menu_navigation.client.RoutePaths
import com.fireavert.react.shared.components.forms.ReactDatePicker
import com.fireavert.react.shared.styles.LoadingCircleStyle
import com.fireavert.react.shared.styles.StandardizedStyles
import com.fireavert.react.yardi.YardiPropertyDataImportModule
import com.fireavert.utilities.getKoinInstance
import com.fireavert.utilities.toLocalDate
import com.fireavert.yardi.common_data.data_models.YardiResidentStatusEnum
import com.fireavert.yardi.shared.logic.data_models.YardiMiniCredentials
import emotion.css.ClassName
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import mui.material.*
import mui.material.styles.TypographyVariant
import mui.system.sx
import react.*
import react.dom.html.ReactHTML
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h3
import react.dom.onChange
import web.cssom.*
import web.dom.ElementId
import web.html.HTMLInputElement
import web.html.InputType
import kotlin.js.Date


data class TestEndpointTab(
    val key: String,
    val label: String,
    // Updated to pass Map directly, interpretation happens in lambda
    val testAction: suspend (credentialId: Int, params: Map<String, Any?>) -> Unit
)

val YardiCommonDataSection = FC<Props> {
    val logger = getKoinInstance<Logger>()
    // Form state for settings
    var formData by useState(YardiSavePropertyDataRequest())
    // Accordion state
    var accordionStates by useState(mapOf(
        "settings" to false,
        "endpoints" to false,
        "serviceRequests" to false
    ))
    var editMode by useState(false)
    // Error states for settings form
    var connectionUrlKeyError by useState(false)
    var usernameError by useState(false)
    var passwordError by useState(false)
    var databaseServerError by useState(false)
    var databaseNameError by useState(false)
    var platformError by useState(false)
    var entityNameError by useState(false)
    var interfaceLicenseError by useState(false)
    var managementCompanyIdError by useState(false)
    var yardiPropertyIdError by useState(false)
    // State for credentials dropdown
    var selectedCredentials by useState("2") // Default to ID 2
    // Loading and response state
    var loading by useState(false)
    var error by useState("")
    var responseData by useState("")
    var credentialOptions: List<YardiMiniCredentials> by useState(emptyList())
    var credentialsInitialized by useState(false)

    // States for general endpoint tab-specific input fields
    var propertyId by useState("resca01") // Common endpoint property ID
    var unitId by useState("")           // Common endpoint unit ID
    var tenantCode by useState("")      // Common endpoint tenant code
    var residentStatus by useState(YardiResidentStatusEnum.CURRENT)
    var dateFrom by useState<Date?>(Date(2010, 0, 1)) // JS Date months are 0-indexed
    var dateTo by useState<Date?>(Date(2025, 0, 1)) // JS Date months are 0-indexed
    var startTime: Date? by useState(dateFrom) // For date picker component
    var endTime: Date? by useState(dateTo)     // For date picker component

    // Controller and ViewModel
    val controller = getKoinInstance<YardiCommonDataController>()
    val viewModel = getKoinInstance<YardiCommonDataViewModel>()

    // State for the active test endpoint tab
    val initialTabKey = "ping"
    var activeTestTab by useState(initialTabKey)

    // ================================================================
    // Service Requests states
    // ================================================================
    val initialServiceRequestTabKey = "srCreateEdit" // Default to create/edit
    var activeServiceRequestTab by useState(initialServiceRequestTabKey)

    // --- State for Service Request Fields ---
    var srServiceRequestId by useState("") // For Get/Edit existing
    var srPropertyId by useState("resca01") // Use a relevant default
    var srUnitId by useState("3107")
    var srTenantId by useState("t0003448")
    var srVendorId by useState("pmbm")
    var srServiceId by useState("") // For Get/Edit existing

    // --- Added/Refined Service Request Fields ---
    var srOrigin by useState("WO") // e.g., "FA" for FireAvert
    var srRoomCode by useState("")
    var srBriefDescription by useState("FireAvert Test SR") // Maps to ServiceRequestBriefDescription
    var srFullDescription by useState("Generated from admin test page.") // Maps to ServiceRequestFullDescription
    var srPriority by useState("Low") // Example: Low, Medium, High, Emergency
    var srCategory by useState("Other")
    var srSubCategory by useState("")
    var srHasPermissionToEnter by useState(true)
    var srAccessNotes by useState("Entry permitted via key.")
    var srProblemDescriptionNotes by useState("Potential issue with smoke detector requires inspection.")
    var srTenantCaused by useState(false)
    var srRequestorName by useState("FireAvert Admin")
    var srRequestorPhoneNumber by useState("555-1234")
    var srRequestorEmail by useState("test@fireavert.com")
    // var srDueDate by useState<Date?>(null) // Optional: Add if needed

    // Format date as YYYY-MM-DD for API consistency (if needed)
    fun formatDateForApi(date: Date?): String? {
        if (date == null) return null
        val year = date.getFullYear().toString()
        val month = (date.getMonth() + 1).toString().padStart(2, '0')
        val day = date.getDate().toString().padStart(2, '0')
        return "$year-$month-$day"
    }

    // ================================================================
    // Tab Definitions
    // ================================================================

    // Define all available endpoint tabs with parameters
    val testEndpointTabs = listOf(
        TestEndpointTab("ping", "Test Ping") { credId, _ ->
            controller.testPing(credId)
        },
        TestEndpointTab("version", "Get Version Number") { credId, _ ->
            controller.testGetVersionNumber(credId)
        },
        TestEndpointTab("unitData", "Test Unit Data") { credId, params ->
            controller.testGetUnitData(credId, params["propertyId"] as String)
        },
        TestEndpointTab("residents", "Test Residents") { credId, params ->
            controller.testGetResidentData(credId, params["propertyId"] as String)
        },
        TestEndpointTab("residentsByStatus", "Test Residents By Status") { credId, params ->
            controller.testGetResidentByStatusData(
                credId,
                params["propertyId"] as String,
                params["status"] as YardiResidentStatusEnum,
                params["dateFrom"] as LocalDate?, // Pass LocalDate directly
                params["dateTo"] as LocalDate?     // Pass LocalDate directly
            )
        },
        TestEndpointTab("propertyList", "Test Get Property List") { credId, params ->
            controller.testGetPropertyList(credId, params["propertyId"] as String)
        },
        TestEndpointTab("residentsByUnit", "Test Get Residents by Unit") { credId, params ->
            controller.testGetResidentsByUnit(
                credId,
                params["propertyId"] as String,
                params["unitId"] as String
            )
        },
        TestEndpointTab("tenantStatus", "Test Get Tenant Status") { credId, params ->
            controller.testGetTenantStatus(credId, params["propertyId"] as String)
        },
        TestEndpointTab("rateLimits", "Test Get Rate Limits") { credId, _ ->
            controller.testGetRateLimits(credId)
        },
        TestEndpointTab("residentsResidential", "Test Get Resident Data (Residential)") { credId, params ->
            controller.testGetResidentDataResidentialOnly(
                credId,
                params["propertyId"] as String,
                params["tenantCode"] as String
            )
        },
        TestEndpointTab("propertyConfigs", "Get Property Configurations") { credId, params ->
            // Try a simple test to see if this works
            controller.getPropertyConfigurations(credId, params["propertyId"] as String)
        },
        // Add other common data endpoints here if needed
    )

    // Define service request tabs with parameters
    val serviceRequestTabs = listOf(
        TestEndpointTab("srVersion", "Get Version Number (SR)") { credId, _ ->
            controller.testGetServiceRequestVersionNumber(credId)
        },
        TestEndpointTab("srPropertyConfigs", "Get Property Configurations (SR)") { credId, params ->
            controller.testGetServiceRequestPropertyConfigurations(
                credId,
                params["propertyId"] as String
            )
        },
        TestEndpointTab("srCreateEdit", "Create or Edit Service Request") { credId, params ->
            // Extract all parameters safely from the map for the controller call
            controller.testCreateEditServiceRequest(
                credentialId = credId,
                serviceRequestId = params["serviceRequestId"] as? String, // Nullable
                propertyCode = params["propertyCode"] as String, // Assuming mandatory
                unitCode = params["unitCode"] as? String,
                tenantCode = params["tenantCode"] as? String,
                vendorCode = params["vendorCode"] as? String,
                briefDescription = params["briefDescription"] as? String,
                fullDescription = params["fullDescription"] as? String,
                accessNotes = params["accessNotes"] as? String,
                origin = params["origin"] as? String,
                roomCode = params["roomCode"] as? String,
                priority = params["priority"] as? String,
                category = params["category"] as? String,
                subCategory = params["subCategory"] as? String,
                hasPermissionToEnter = params["hasPermissionToEnter"] as? Boolean,
                problemDescriptionNotes = params["problemDescriptionNotes"] as? String,
                tenantCaused = params["tenantCaused"] as? Boolean,
                requestorName = params["requestorName"] as? String,
                requestorPhoneNumber = params["requestorPhoneNumber"] as? String,
                requestorEmail = params["requestorEmail"] as? String
                // dueDate = params["dueDate"] as? LocalDate // Add if using DatePicker
            )
        },
        TestEndpointTab("srGet", "Get Service Request(s)") { credId, params ->
            controller.testGetServiceRequests(
                credId,
                params["propertyId"] as String,
                params["serviceId"] as? String // Allow nullable Service ID for getting multiple
            )
        }
    )

    // ================================================================
    // Effects and Handlers
    // ================================================================

    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            loading = viewState.loading
            error = viewState.error
            responseData = viewState.data
            credentialOptions = viewState.availableCredentials

            if (!credentialsInitialized && viewState.availableCredentials.isNotEmpty()) {
                // Try to find ID 2, otherwise default to the first one if available
                val defaultCred = viewState.availableCredentials.find { it.id == 2 }?.id?.toString()
                    ?: viewState.availableCredentials.firstOrNull()?.id?.toString()
                    ?: "" // Fallback to empty string if no creds
                selectedCredentials = defaultCred
                credentialsInitialized = true
                logger.i("Credentials initialized. Default set to ID: $defaultCred")
            }
        }.launchIn(viewModel.scope)

        val onLoadJob = viewModel.scope.launch { controller.onLoad() }

        onCleanup {
            viewStateJob.cancel()
            onLoadJob.cancel()
        }
    }

    val handleCredentialsChange = useCallback<(dynamic, dynamic) -> Unit> { event, _ ->
        selectedCredentials = event.target.value as String
        logger.i("Selected credentials ID: $selectedCredentials")
    }

    fun validateForm(): Boolean {
        var isValid = true
        connectionUrlKeyError = false; usernameError = false; passwordError = false; databaseServerError = false; databaseNameError = false; platformError = false; entityNameError = false; interfaceLicenseError = false; managementCompanyIdError = false; yardiPropertyIdError = false
        if (formData.connectionUrlKey.isBlank()) { connectionUrlKeyError = true; isValid = false }
        if (formData.username.isBlank()) { usernameError = true; isValid = false }
        if (formData.password.isBlank()) { passwordError = true; isValid = false }
        if (formData.databaseServer.isBlank()) { databaseServerError = true; isValid = false }
        if (formData.databaseName.isBlank()) { databaseNameError = true; isValid = false }
        if (formData.platform.isBlank()) { platformError = true; isValid = false }
        if (formData.entityName.isBlank()) { entityNameError = true; isValid = false }
        if (formData.interfaceLicense.isBlank()) { interfaceLicenseError = true; isValid = false }
        if (formData.yardiPropertyId.isBlank()) { yardiPropertyIdError = true; isValid = false }
        if (formData.managementCompanyId <= 0) { managementCompanyIdError = true; isValid = false }
        return isValid
    }

    fun toggleAccordion(key: String) {
        accordionStates = accordionStates.toMutableMap().apply {
            this[key] = !(this[key] ?: false)
        }
    }

    // Reset common endpoint fields when tab changes
    fun resetInputFields() {
        propertyId = "resca01"
        unitId = ""
        tenantCode = ""
        residentStatus = YardiResidentStatusEnum.CURRENT
        dateFrom = Date(2010, 0, 1)
        dateTo = Date(2025, 0, 1)
        startTime = dateFrom
        endTime = dateTo
    }

    // Reset service request fields when SR tab changes
    fun resetServiceRequestFields() {
        // Reset to reasonable defaults for SR
        srServiceRequestId = ""
        srPropertyId = "resca01"
        srUnitId = "3107"
        srTenantId = "t0003448"
        srVendorId = "pmbm"
        srServiceId = ""
        srOrigin = "WO"
        srRoomCode = ""
        srBriefDescription = "FireAvert Test SR"
        srFullDescription = "Generated from admin test page."
        srPriority = "Low"
        srCategory = "Other"
        srSubCategory = ""
        srHasPermissionToEnter = true
        srAccessNotes = "Entry permitted via key."
        srProblemDescriptionNotes = "Potential issue with smoke detector requires inspection."
        srTenantCaused = false
        srRequestorName = "FireAvert Admin"
        srRequestorPhoneNumber = "555-1234"
        srRequestorEmail = "test@fireavert.com"
        // srDueDate = null // Reset if using date picker
    }

    // ================================================================
    // Render UI
    // ================================================================
    Breadcrumbs {
        ariaLabel = "breadcrumb"
        Link { underline = LinkUnderline.hover; color = "inherit"; href = RoutePaths.administration; +"Administration" }
        Typography { sx { color = NamedColor.black }; +"Yardi Integration" }
    }

    // Header with Title and Edit Button (for Settings)
    div {
        className = ClassName { display = Display.flex; justifyContent = JustifyContent.spaceBetween; alignItems = AlignItems.center; marginBottom = 24.px; media(MediaQuery("(max-width: 640px)")) { flexDirection = FlexDirection.column; gap = 16.px } }
        div { className = StandardizedStyles.HeaderH2; +"Yardi Integration" }
        Button {
            sx { backgroundColor = if (!editMode) NamedColor.black else null; color = if (!editMode) NamedColor.white else null; media(MediaQuery("(max-width: 640px)")) { marginTop = 10.px } }
            variant = ButtonVariant.contained
            onClick = {
                if (editMode) { if (validateForm()) { viewModel.scope.launch { controller.savePropertyData(formData); editMode = false }; logger.i("Attempting to save settings...") } else { logger.w("Form validation failed.") } } else { editMode = true }
            }
            +(if (editMode) "Save Settings" else "Edit Settings")
        }
    }

    div { // Main content container
        className = ClassName { width = 100.pct; margin = Margin(0.px, Auto.auto) }

        // Settings Accordion (remains unchanged)
        Accordion {
            expanded = accordionStates["settings"] == true
            onChange = { _, isExpanded -> toggleAccordion("settings") }
            AccordionSummary { +"Integration Settings" }
            AccordionDetails {
                // ... (Settings form fields as before) ...
                div { className = ClassName { display = Display.grid; gap = 16.px }
                    // Meta Settings
                    div { h3 { className = ClassName { fontSize = 16.px; fontWeight = integer(600); marginBottom = 16.px; color = rgb(59, 59, 59) }; +"Meta Settings" }; div { TextField { label = ReactNode("Name of Credentials"); fullWidth = true; value = formData.credentialName; disabled = !editMode; onChange = { event -> formData = formData.copy(credentialName = (event.target as HTMLInputElement).value) }; helperText = if (connectionUrlKeyError) ReactNode("Required field") else ReactNode("") } } }
                    // Connection Settings
                    div { h3 { className = ClassName { fontSize = 16.px; fontWeight = integer(600); marginBottom = 16.px; color = rgb(59, 59, 59) }; +"Connection Settings" }; div { className = ClassName { display = Display.grid; gap = 16.px }; TextField { label = ReactNode("Connection URL Key"); fullWidth = true; value = formData.connectionUrlKey; disabled = !editMode; onChange = { event -> formData = formData.copy(connectionUrlKey = (event.target as HTMLInputElement).value) }; helperText = if (connectionUrlKeyError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Username"); fullWidth = true; value = formData.username; disabled = !editMode; onChange = { event -> formData = formData.copy(username = (event.target as HTMLInputElement).value) }; helperText = if (usernameError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Password"); fullWidth = true; value = formData.password; disabled = !editMode; onChange = { event -> formData = formData.copy(password = (event.target as HTMLInputElement).value) }; helperText = if (passwordError) ReactNode("Required field") else ReactNode("") } } }
                    // Database Settings
                    div { h3 { className = ClassName { fontSize = 16.px; fontWeight = integer(600); marginBottom = 16.px; color = rgb(59, 59, 59) }; +"Database Settings" }; div { className = ClassName { display = Display.grid; gap = 16.px }; TextField { label = ReactNode("Database Server"); fullWidth = true; value = formData.databaseServer; disabled = !editMode; onChange = { event -> formData = formData.copy(databaseServer = (event.target as HTMLInputElement).value) }; helperText = if (databaseServerError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Database Name"); fullWidth = true; value = formData.databaseName; disabled = !editMode; onChange = { event -> formData = formData.copy(databaseName = (event.target as HTMLInputElement).value) }; helperText = if (databaseNameError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Platform"); fullWidth = true; value = formData.platform; disabled = !editMode; onChange = { event -> formData = formData.copy(platform = (event.target as HTMLInputElement).value) }; helperText = if (platformError) ReactNode("Required field") else ReactNode("") } } }
                    // Yardi Settings
                    div { h3 { className = ClassName { fontSize = 16.px; fontWeight = integer(600); marginBottom = 16.px; color = rgb(59, 59, 59) }; +"Yardi Settings" }; div { className = ClassName { display = Display.grid; gap = 16.px }; TextField { label = ReactNode("Entity Name"); fullWidth = true; value = formData.entityName; disabled = !editMode; onChange = { event -> formData = formData.copy(entityName = (event.target as HTMLInputElement).value) }; helperText = if (entityNameError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Interface License"); fullWidth = true; value = formData.interfaceLicense; disabled = !editMode; onChange = { event -> formData = formData.copy(interfaceLicense = (event.target as HTMLInputElement).value) }; helperText = if (interfaceLicenseError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Management Company ID"); fullWidth = true; type = InputType.number; value = formData.managementCompanyId.toString(); disabled = !editMode; onChange = { event -> formData = formData.copy(managementCompanyId = (event.target as HTMLInputElement).value.toIntOrNull() ?: 0) }; helperText = if (managementCompanyIdError) ReactNode("Required field") else ReactNode("") }; TextField { label = ReactNode("Yardi Property ID"); fullWidth = true; value = formData.yardiPropertyId; disabled = !editMode; onChange = { event -> formData = formData.copy(yardiPropertyId = (event.target as HTMLInputElement).value) }; helperText = if (yardiPropertyIdError) ReactNode("Required field") else ReactNode("") } } }
                }
            }
        }

        // Credentials Selection Dropdown
        h3 { className = ClassName { fontSize = 16.px; fontWeight = FontWeight.bolder; marginBottom = 8.px; marginTop = 24.px; color = rgb(59, 59, 59) }; +"Select Credentials to Test" }
        FormControl {
            fullWidth = true
            sx { minWidth = 200.px; marginBottom = 16.px }
            InputLabel { id = ElementId("credentials-select-label"); +"Yardi Credentials" }
            Select {
                labelId = "credentials-select-label"
                id = ElementId("credentials-select")
                value = selectedCredentials
                label = ReactNode("Yardi Credentials")
                onChange = handleCredentialsChange
                if (credentialOptions.isEmpty() && !credentialsInitialized) {
                    MenuItem { disabled = true; +"Loading credentials..." }
                } else if (credentialOptions.isEmpty()) {
                    MenuItem { disabled = true; +"No credentials found." }
                } else {
                    credentialOptions.forEach { option ->
                        MenuItem { key = option.id.toString(); value = option.id.toString(); +option.name }
                    }
                }
            }
        }

        // Test Endpoints Accordion (Common Data)
        div { className = StandardizedStyles.HeaderH3; +"Yardi Endpoint Tests" }
        Accordion {
            expanded = accordionStates["endpoints"] == true
            onChange = { _, isExpanded -> toggleAccordion("endpoints") }
            AccordionSummary { +"Test Endpoints (Common Data)" } // Renamed for clarity
            AccordionDetails {
                // Tabs navigation
                Box { sx { borderBottom = Border(1.px, LineStyle.solid, Color("#e0e0e0")); marginBottom = 16.px }
                    Tabs {
                        value = activeTestTab
                        onChange = { _, newValue -> activeTestTab = newValue as String; resetInputFields() }
                        variant = TabsVariant.scrollable; scrollButtons = TabsScrollButtons.auto; allowScrollButtonsMobile = true; ariaLabel = "Yardi Endpoint Tests"
                        testEndpointTabs.forEach { tabInfo -> Tab { key = tabInfo.key; value = tabInfo.key; label = ReactNode(tabInfo.label) } }
                    }
                }
                // Tab content area
                div { className = ClassName { marginTop = 16.px; minHeight = 60.px }
                    val currentTabInfo = testEndpointTabs.find { it.key == activeTestTab }
                    if (currentTabInfo != null) {
                        div { className = ClassName { display = Display.flex; flexDirection = FlexDirection.column; gap = 16.px; alignItems = AlignItems.flexStart }
                            // Input fields based on tab type
                            when (currentTabInfo.key) {
                                "unitData", "residents", "tenantStatus", "propertyList", "propertyConfigs" -> {
                                    TextField { label = ReactNode("Property ID"); value = propertyId; onChange = { event -> propertyId = (event.target as HTMLInputElement).value }; fullWidth = true; sx { marginBottom = 16.px } }
                                }
                                "residentsByUnit" -> {
                                    div { className = ClassName { display = Display.grid; gridTemplateColumns = repeat(2, 1.fr.unsafeCast<GridTemplateTracks>()); gap = 16.px; width = 100.pct; marginBottom = 16.px }
                                        TextField { label = ReactNode("Property ID"); value = propertyId; onChange = { event -> propertyId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Unit ID"); value = unitId; onChange = { event -> unitId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                    }
                                }
                                "residentsResidential" -> {
                                    div { className = ClassName { display = Display.grid; gridTemplateColumns = repeat(2, 1.fr.unsafeCast<GridTemplateTracks>()); gap = 16.px; width = 100.pct; marginBottom = 16.px }
                                        TextField { label = ReactNode("Property ID"); value = propertyId; onChange = { event -> propertyId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Tenant Code"); value = tenantCode; onChange = { event -> tenantCode = (event.target as HTMLInputElement).value }; fullWidth = true }
                                    }
                                }
                                "residentsByStatus" -> {
                                    div { className = ClassName { display = Display.grid; gridTemplateColumns = repeat(4, 1.fr.unsafeCast<GridTemplateTracks>()); gap = 16.px; width = 100.pct; marginBottom = 16.px; alignItems = AlignItems.center } // Align items vertically
                                        TextField { label = ReactNode("Property ID"); value = propertyId; onChange = { event -> propertyId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        FormControl {
                                            fullWidth = true; InputLabel {
                                            id = ElementId("status-select-label"); +"Resident Status"
                                        }
                                            Select {
                                                labelId = "status-select-label"; id =
                                                ElementId("status-select"); value = residentStatus; label =
                                                ReactNode("Resident Status"); onChange = { event, _ ->
                                                residentStatus = event.target.value as YardiResidentStatusEnum
                                            }
                                                MenuItem { value = YardiResidentStatusEnum.ALL; +"All" }; MenuItem { value = YardiResidentStatusEnum.APPLICANT; +"Applicant" }; MenuItem { value = YardiResidentStatusEnum.CURRENT; +"Current" }; MenuItem { value = YardiResidentStatusEnum.FUTURE; +"Future" }; MenuItem { value = YardiResidentStatusEnum.NOTICE; +"Notice" }; MenuItem { value = YardiResidentStatusEnum.EVICTION; +"Eviction" }; MenuItem { value = YardiResidentStatusEnum.PAST; +"Past" }; MenuItem { value = YardiResidentStatusEnum.CANCELLED; +"Canceled" }; MenuItem { value = YardiResidentStatusEnum.DENIED; +"Denied" }
                                            }
                                        }
                                        // Date Picker wrapper
                                        div {  } // Span 2 columns
                                        Typography { variant = TypographyVariant.caption; sx { display = Display.block; marginBottom = 4.px }; +"Date Range (Optional)"}
                                        ReactDatePicker {
                                            selectsRange = true; swapRange = true; todayButton = "Today"
                                            onChange = { dates -> startTime = dates[0]; endTime = dates[1]; dateFrom = dates[0]; dateTo = dates[1] }
                                            this.startDate = startTime; this.endDate = endTime;
                                        }
                                    }
                                }
                                // Default case for tabs with no specific inputs (like ping, version)
                                else -> { Typography { +"Click Run button below." } }
                            }
                        }
                        // Button to trigger the common endpoint test
                        Button {
                            variant = ButtonVariant.contained; color = ButtonColor.primary; disabled = selectedCredentials.isBlank()
                            onClick = {
                                val credId = selectedCredentials.toIntOrNull()
                                if (credId != null) {
                                    error = ""; responseData = ""
                                    // Collect parameters specific to the active tab
                                    val params = mutableMapOf<String, Any?>()
                                    when (currentTabInfo.key) {
                                        "unitData", "residents", "tenantStatus", "propertyList", "propertyConfigs" -> params["propertyId"] = propertyId
                                        "residentsByUnit" -> { params["propertyId"] = propertyId; params["unitId"] = unitId }
                                        "residentsResidential" -> { params["propertyId"] = propertyId; params["tenantCode"] = tenantCode }
                                        "residentsByStatus" -> { params["propertyId"] = propertyId; params["status"] = residentStatus; params["dateFrom"] = dateFrom?.toLocalDate(); params["dateTo"] = dateTo?.toLocalDate() }
                                    }
                                    viewModel.scope.launch {
                                        try { currentTabInfo.testAction(credId, params) }
                                        catch (e: Exception) { logger.e("Error launching test action: ${e.message}", e); error = "Client-side error: ${e.message}"; loading = false }
                                    }
                                } else { error = "Invalid or no credentials selected."; logger.w("Attempted to run test without valid credentials ID.") }
                            }
                            sx { marginTop = 8.px }; +"Run: ${currentTabInfo.label}"
                        }
                    } else {
                        Typography { +"Select a tab to test an endpoint." }
                    }
                }
            }
            // Response Display Section (Shared) - No changes needed here
            // ... (Response display logic as before) ...
            div { className = ClassName { marginTop = 24.px; borderTop = Border(1.px, LineStyle.solid, Color("#edeff2")); paddingTop = 24.px }; Typography { variant = TypographyVariant.h6; +"Response" }; if (error.isNotEmpty()) { Alert { color = AlertColor.error; +error } }; if (loading) { div { className = LoadingCircleStyle.loadingCircleAdminPage; CircularProgress { color = CircularProgressColor.primary } } } else if (responseData.isNotEmpty()) { div { className = ClassName { marginTop = 16.px; padding = 16.px; backgroundColor = rgb(245, 245, 245); borderRadius = 4.px; minHeight = 100.px; maxHeight = 600.px; overflowY = Auto.auto; whiteSpace = WhiteSpace.preWrap; wordBreak = WordBreak.breakAll }; Typography { component = ReactHTML.pre; sx { marginTop = 8.px; fontFamily = FontFamily.monospace }; +responseData } } } else if (!loading && error.isEmpty()) { div { className = ClassName { marginTop = 16.px; padding = 16.px; backgroundColor = rgb(245, 245, 245); borderRadius = 4.px; minHeight = 100.px; display = Display.flex; alignItems = AlignItems.center; justifyContent = JustifyContent.center }; Typography { +"Run a test using the button above to see the response." } } } }
        }
    }

    // ================================================================
    // Yardi Service Requests Accordion - UPDATED SECTION
    // ================================================================
    Accordion {
        expanded = accordionStates["serviceRequests"] == true
        onChange = { _, isExpanded -> toggleAccordion("serviceRequests") }
        AccordionSummary { +"Yardi Service Requests" }
        AccordionDetails {
            // Tabs navigation for Service Requests
            Box { sx { borderBottom = Border(1.px, LineStyle.solid, Color("#e0e0e0")); marginBottom = 16.px }
                Tabs {
                    value = activeServiceRequestTab
                    onChange = { _, newValue -> activeServiceRequestTab = newValue as String; resetServiceRequestFields() }
                    variant = TabsVariant.scrollable; scrollButtons = TabsScrollButtons.auto; allowScrollButtonsMobile = true; ariaLabel = "Yardi Service Request Tests"
                    serviceRequestTabs.forEach { tabInfo -> Tab { key = tabInfo.key; value = tabInfo.key; label = ReactNode(tabInfo.label) } }
                }
            }

            // Service Request Tab content area
            div { className = ClassName { marginTop = 16.px; minHeight = 60.px }
                val currentTabInfo = serviceRequestTabs.find { it.key == activeServiceRequestTab }
                if (currentTabInfo != null) {
                    div { className = ClassName { display = Display.flex; flexDirection = FlexDirection.column; gap = 16.px; alignItems = AlignItems.flexStart }
                        // --- Input fields based on Service Request tab type ---
                        when (currentTabInfo.key) {
                            "srVersion" -> { Typography { +"Click the button below to get the Service Requests version number." } }
                            "srPropertyConfigs" -> {
                                TextField { label = ReactNode("Property ID *"); value = srPropertyId; required = true; onChange = { event -> srPropertyId = (event.target as HTMLInputElement).value }; fullWidth = true; sx { marginBottom = 16.px } }
                            }
                            "srCreateEdit" -> { // --- UPDATED LAYOUT ---
                                div { // Main container for the form grid
                                    className = ClassName { display = Display.grid; gridTemplateColumns = repeat(3, 1.fr.unsafeCast<GridTemplateTracks>()); gap = 16.px; width = 100.pct }

                                    // --- Column 1: Core Identifiers & Location ---
                                    div { className = ClassName { display = Display.flex; flexDirection = FlexDirection.column; gap = 16.px }
                                        TextField { label = ReactNode("Service Request ID (If Editing)"); value = srServiceRequestId; onChange = { event -> srServiceRequestId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Property ID *"); value = srPropertyId; required = true; onChange = { event -> srPropertyId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Unit ID"); value = srUnitId; onChange = { event -> srUnitId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Tenant ID"); value = srTenantId; onChange = { event -> srTenantId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Room Code"); value = srRoomCode; onChange = { event -> srRoomCode = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Vendor ID"); value = srVendorId; onChange = { event -> srVendorId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Origin"); value = srOrigin; onChange = { event -> srOrigin = (event.target as HTMLInputElement).value }; fullWidth = true; helperText=ReactNode("e.g., FA, SG, WO") }
                                    }

                                    // --- Column 2: Description & Details ---
                                    div { className = ClassName { display = Display.flex; flexDirection = FlexDirection.column; gap = 16.px }
                                        TextField { label = ReactNode("Brief Description"); value = srBriefDescription; onChange = { event -> srBriefDescription = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Full Description"); value = srFullDescription; onChange = { event -> srFullDescription = (event.target as HTMLInputElement).value }; fullWidth = true;}
                                        TextField { label = ReactNode("Problem Description Notes"); value = srProblemDescriptionNotes; onChange = { event -> srProblemDescriptionNotes = (event.target as HTMLInputElement).value }; fullWidth = true; }
                                        TextField { label = ReactNode("Access Notes"); value = srAccessNotes; onChange = { event -> srAccessNotes = (event.target as HTMLInputElement).value }; fullWidth = true; }
                                        TextField { label = ReactNode("Priority"); value = srPriority; onChange = { event -> srPriority = (event.target as HTMLInputElement).value }; fullWidth = true; helperText=ReactNode("e.g., Low") }
                                        TextField { label = ReactNode("Category"); value = srCategory; onChange = { event -> srCategory = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("SubCategory"); value = srSubCategory; onChange = { event -> srSubCategory = (event.target as HTMLInputElement).value }; fullWidth = true }
                                    }

                                    // --- Column 3: Permissions, Requestor & Flags ---
                                    div { className = ClassName { display = Display.flex; flexDirection = FlexDirection.column; gap = 16.px }
                                        FormControlLabel { label = ReactNode("Permission to Enter?"); control = Checkbox.create { checked = srHasPermissionToEnter; onChange = { _, checked -> srHasPermissionToEnter = checked } } }
                                        FormControlLabel { label = ReactNode("Tenant Caused?"); control = Checkbox.create { checked = srTenantCaused; onChange = { _, checked -> srTenantCaused = checked } } }
                                        TextField { label = ReactNode("Requestor Name"); value = srRequestorName; onChange = { event -> srRequestorName = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Requestor Phone Number"); value = srRequestorPhoneNumber; onChange = { event -> srRequestorPhoneNumber = (event.target as HTMLInputElement).value }; fullWidth = true }
                                        TextField { label = ReactNode("Requestor Email"); value = srRequestorEmail; onChange = { event -> srRequestorEmail = (event.target as HTMLInputElement).value }; fullWidth = true; type = InputType.email }
                                        // Optional: Add DueDate DatePicker here if needed
                                    }
                                } // End of main grid div
                            } // End of "srCreateEdit" case

                            "srGet" -> {
                                div { className = ClassName { display = Display.grid; gridTemplateColumns = repeat(2, 1.fr.unsafeCast<GridTemplateTracks>()); gap = 16.px; width = 100.pct; marginBottom = 16.px }
                                    TextField { label = ReactNode("Property ID *"); value = srPropertyId; required = true; onChange = { event -> srPropertyId = (event.target as HTMLInputElement).value }; fullWidth = true }
                                    TextField { label = ReactNode("Service ID *"); value = srServiceId; onChange = { event -> srServiceId = (event.target as HTMLInputElement).value }; fullWidth = true; helperText = ReactNode("Please select one") }
                                }
                            }
                        } // End When

                        // --- Button to trigger the service request test ---
                        Button {
                            variant = ButtonVariant.contained; color = ButtonColor.primary; disabled = selectedCredentials.isBlank()
                            onClick = {
                                val credId = selectedCredentials.toIntOrNull()
                                if (credId != null) {
                                    error = ""; responseData = ""
                                    // Collect parameters specific to the active SR tab
                                    val params = mutableMapOf<String, Any?>()
                                    when (currentTabInfo.key) {
                                        "srPropertyConfigs" -> { params["propertyId"] = srPropertyId }
                                        "srCreateEdit" -> {
                                            params["serviceRequestId"] = srServiceRequestId.takeIf { it.isNotBlank() }
                                            params["propertyCode"] = srPropertyId // Match controller arg name
                                            params["unitCode"] = srUnitId.takeIf { it.isNotBlank() }
                                            params["tenantCode"] = srTenantId.takeIf { it.isNotBlank() }
                                            params["vendorCode"] = srVendorId.takeIf { it.isNotBlank() }
                                            params["briefDescription"] = srBriefDescription.takeIf { it.isNotBlank() }
                                            params["fullDescription"] = srFullDescription.takeIf { it.isNotBlank() }
                                            params["accessNotes"] = srAccessNotes.takeIf { it.isNotBlank() }
                                            params["origin"] = srOrigin.takeIf { it.isNotBlank() }
                                            params["roomCode"] = srRoomCode.takeIf { it.isNotBlank() }
                                            params["priority"] = srPriority.takeIf { it.isNotBlank() }
                                            params["category"] = srCategory.takeIf { it.isNotBlank() }
                                            params["subCategory"] = srSubCategory.takeIf { it.isNotBlank() }
                                            params["hasPermissionToEnter"] = srHasPermissionToEnter
                                            params["problemDescriptionNotes"] = srProblemDescriptionNotes.takeIf { it.isNotBlank() }
                                            params["tenantCaused"] = srTenantCaused
                                            params["requestorName"] = srRequestorName.takeIf { it.isNotBlank() }
                                            params["requestorPhoneNumber"] = srRequestorPhoneNumber.takeIf { it.isNotBlank() }
                                            params["requestorEmail"] = srRequestorEmail.takeIf { it.isNotBlank() }
                                            // params["dueDate"] = formatDateForApi(srDueDate) // Add if using DatePicker
                                        }
                                        "srGet" -> { params["propertyId"] = srPropertyId; params["serviceId"] = srServiceId.takeIf { it.isNotBlank() } }
                                    }
                                    viewModel.scope.launch {
                                        try { currentTabInfo.testAction(credId, params) }
                                        catch (e: Exception) { logger.e("Error launching service request test: ${e.message}", e); error = "Client-side error: ${e.message}"; loading = false }
                                    }
                                } else { error = "Invalid or no credentials selected."; logger.w("Attempted to run service request test without valid credentials ID.") }
                            }
                            sx { marginTop = 16.px }; +"Run: ${currentTabInfo.label}"
                        }
                    }
                } else { Typography { +"Select a tab to test a service request endpoint." } }
            }

            div { className = ClassName { marginTop = 24.px; borderTop = Border(1.px, LineStyle.solid, Color("#edeff2")); paddingTop = 24.px }; Typography { variant = TypographyVariant.h6; +"Response" }; if (error.isNotEmpty()) { Alert { color = AlertColor.error; +error } }; if (loading) { div { className = LoadingCircleStyle.loadingCircleAdminPage; CircularProgress { color = CircularProgressColor.primary } } } else if (responseData.isNotEmpty()) { div { className = ClassName { marginTop = 16.px; padding = 16.px; backgroundColor = rgb(245, 245, 245); borderRadius = 4.px; minHeight = 100.px; maxHeight = 600.px; overflowY = Auto.auto; whiteSpace = WhiteSpace.preWrap; wordBreak = WordBreak.breakAll }; Typography { component = ReactHTML.pre; sx { marginTop = 8.px; fontFamily = FontFamily.monospace }; +responseData } } } else if (!loading && error.isEmpty()) { div { className = ClassName { marginTop = 16.px; padding = 16.px; backgroundColor = rgb(245, 245, 245); borderRadius = 4.px; minHeight = 100.px; display = Display.flex; alignItems = AlignItems.center; justifyContent = JustifyContent.center }; Typography { +"Run a test using the button above to see the response." } } } }

        }
    } // End Service Requests Accordion

    div { className = StandardizedStyles.HeaderH3; +"Yardi Import Property Data" }


    //Yardi Property Data Import Section
    YardiPropertyDataImportModule {
        this.yardiCredentials = credentialOptions
    }

} // End Main content container

    